<template>
  <ProductContentDetails
    v-if="offer"
    :offer="offer"
    headline="Produktsicherheit"
    class="product-security"
    :render-item-if="(product) => getList(product)?.length > 0"
  >
    <template #default="slotProps">
      <ProductTabList
        v-if="slotProps.product"
        :list="getList(slotProps.product)"
        class="unstyled-html"
      />
    </template>
  </ProductContentDetails>
</template>

<script setup lang="ts">
import ProductContentDetails from '~/components/product-detail/tabs/product-content-details.vue';
import ProductTabList, {
  type ProductTabListType,
} from '~/components/product-detail/tabs/product-tab-list.vue';
import type { Warranty } from '#shared/types/goliath/goliath.types';

export type ProductSecurityProps = {
  offer?: Offer | undefined;
};

withDefaults(defineProps<ProductSecurityProps>(), {
  offer: undefined,
});

const getList = (product: OfferProduct | null) => {
  const items: ProductTabListType[] = [];
  const standardProduct = getStandardProduct(product);
  const family = standardProduct?.family;
  if (family) {
    if ('manufacturerContact' in family && family.manufacturerContact) {
      items.push({
        name: 'Herstellerinformationen',
        value: family.manufacturerContact,
      });
    }
    if ('manufacturerContactEu' in family && family.manufacturerContactEu) {
      items.push({
        name: 'Verantwortliche Person für die EU',
        value: family.manufacturerContactEu,
      });
    }
  }
  if (standardProduct?.securityInformationUrl) {
    items.push({
      name: '',
      value: `<a href="${standardProduct.securityInformationUrl}" target="_blank" rel="nofollow">Sicherheitsdatenblatt auf der Webseite des Herstellers</a>`,
    });
  }
  const warranty = family && 'warranty' in family ? (family.warranty as Warranty) : undefined;
  const productName = standardProduct?.name;
  if (warranty?.additionalCommercialGuarantee && productName) {
    items.push({
      name: 'Herstellergarantie',
      value: `Der Hersteller gewährt Ihnen für <strong>${productName}</strong> eine Haltbarkeitsgarantie von <strong>${warranty.additionalCommercialGuarantee}</strong> Jahren. Daneben besteht Ihr gesetzliches Gewährleistungsrecht.`,
    });
  }
  if (warranty?.softwareUpdates && productName) {
    items.push({
      name: 'Software-Updates',
      value: `Für <strong>${productName}</strong> stellt der Hersteller mindestens <strong>${warranty.softwareUpdates} Jahre</strong> Softwareaktualisierungen bereit.`,
    });
  }
  if (warranty?.repairabilityClass && productName) {
    items.push({
      name: 'Reparierbarkeit',
      value: `Der Reparierbarkeitswert von <strong>${productName}</strong> beträgt <strong>${warranty.repairabilityClass}</strong>.`,
    });
  }
  return items;
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;
</style>
